import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { NavigationGuardContext } from './contexts/NavigationGuardContext';
import { canOpenPage, firstAllowedPage } from './navigation';
import { consumeBack, afterPendingBack, useBackHandler } from './utils/backStack';
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import Sidebar from './components/Sidebar';
import { brand } from './brand';

// Pages load on demand so the first visit downloads only what it shows
const Dashboard = lazy(() => import('./components/Dashboard'));
const StockPage = lazy(() => import('./components/StockPage'));
const ClientsPage = lazy(() => import('./components/ClientsPage'));
const NewDocumentPage = lazy(() => import('./components/NewDocumentPage'));
const ViewDocumentPage = lazy(() => import('./components/ViewDocumentPage'));
const ProformasPage = lazy(() => import('./components/ProformasPage'));
const InvoicesPage = lazy(() => import('./components/InvoicesPage'));
const SettingsPage = lazy(() => import('./components/SettingsPage'));
const AccountingPage = lazy(() => import('./components/AccountingPage'));
const PaymentsPage = lazy(() => import('./components/PaymentsPage'));
const ExpensesPage = lazy(() => import('./components/ExpensesPage'));
const AdminPage = lazy(() => import('./components/AdminPage'));
const ServicesPage = lazy(() => import('./components/ServicesPage'));

// Pages that can be restored from the URL (#/invoices). Document view/edit need
// in-memory data, so their URL entries fall back to the list they came from.
const PAGE_TITLES = {
    dashboard: 'Dashboard',
    proformas: 'Proformas',
    invoices: 'Invoices',
    payments: 'Payments',
    expenses: 'Expenses',
    stock: 'Stock',
    services: 'Services',
    clients: 'Clients',
    accounting: 'Accounting',
    settings: 'Settings',
    admin: 'Admin',
    newDocument: 'New Document',
    viewDocument: 'Document',
};
const RESTORABLE_PAGES = Object.keys(PAGE_TITLES).filter((p) => p !== 'viewDocument');

const pageFromHash = () => {
    const name = window.location.hash.replace(/^#\/?/, '');
    return RESTORABLE_PAGES.includes(name) ? name : null;
};

const hashFor = (page, editingDocument) =>
    page === 'viewDocument' ? '#/view' : page === 'newDocument' && editingDocument ? '#/edit' : `#/${page}`;

const PageLoader = () => (
    <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
    </div>
);

function AppContent() {
    const { user, loading, can, isAdmin } = useAuth();
    const { settings, servicesEnabled } = useSettings();
    const access = { can, isAdmin, servicesEnabled };
    const [page, setPage] = useState(() => pageFromHash() || 'dashboard');
    const [editingDocument, setEditingDocument] = useState(null);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [previousPage, setPreviousPage] = useState(null);

    // A screen with unsaved work (the document editor) registers a guard that
    // decides whether, and when, navigation may continue.
    const guardRef = useRef(null);
    const setNavigationGuard = useCallback((guard) => {
        guardRef.current = guard;
    }, []);

    const goTo = (pageName, data = null) => {
        if (pageName === 'viewDocument' && data) {
            setPreviousPage(page);
            setEditingDocument(data);
        } else if (pageName === 'newDocument' && data) {
            setPreviousPage(page);
            setEditingDocument(data);
        } else {
            setEditingDocument(null);
            if (page === 'viewDocument' || page === 'newDocument') {
                setPreviousPage(null);
            }
        }
        setPage(pageName);
    };

    const navigateTo = (pageName, data = null) => {
        if (guardRef.current) {
            guardRef.current(() => goTo(pageName, data));
            return;
        }
        goTo(pageName, data);
    };

    // The mobile menu closes with the browser Back button too
    useBackHandler(isSidebarOpen, () => setSidebarOpen(false));

    // A user without the Dashboard lands on their first available section
    useEffect(() => {
        if (user && page === 'dashboard' && !can('dashboard')) {
            const home = firstAllowedPage({ can, isAdmin, servicesEnabled });
            if (home) setPage(home);
        }
    }, [user, page, can, isAdmin, servicesEnabled]);

    // Keep the URL in sync so refresh and the browser back button work
    const isFirstSync = useRef(true);
    useEffect(() => {
        const hash = hashFor(page, editingDocument);
        const first = isFirstSync.current;
        isFirstSync.current = false;
        afterPendingBack(() => {
            if (window.location.hash !== hash) {
                // Don't add a history entry for the initial page
                window.history[first ? 'replaceState' : 'pushState'](null, '', hash);
            }
        });
    }, [page, editingDocument]);

    useEffect(() => {
        const onPopState = () => {
            // An open modal / sub-view closes first
            if (consumeBack()) return;

            const target = pageFromHash() || previousPage || 'dashboard';
            if (guardRef.current) {
                // Stay put (restore our URL) until the guard lets us leave
                window.history.pushState(null, '', hashFor(page, editingDocument));
                guardRef.current(() => goTo(target));
                return;
            }
            setEditingDocument(null);
            setPage(target);
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
        // eslint-disable-next-line
    }, [previousPage, page, editingDocument]);

    const renderPage = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center h-screen">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
            );
        }

        if (!user) {
            return brand.showMarketingLanding ? <LandingPage /> : <LoginPage />;
        }

        // Services depend on a setting; wait for it before deciding access
        const settingsReady = settings !== null;
        const home = firstAllowedPage(access);
        const pageAllowed = canOpenPage(page, access);

        return (
            <ErrorBoundary>
                <div className="relative min-h-screen md:flex">
                    {/* Mobile top bar */}
                    <div className="md:hidden sticky top-0 z-30 flex justify-between items-center px-4 py-3 bg-gray-800 text-white shadow">
                        <h1 className="text-lg font-bold truncate">{PAGE_TITLES[page] || page}</h1>
                        <button onClick={() => setSidebarOpen(!isSidebarOpen)} aria-label="Open menu" aria-expanded={isSidebarOpen} className="p-1">
                            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
                            </svg>
                        </button>
                    </div>

                    {/* Tap outside the mobile menu to close it */}
                    {isSidebarOpen && (
                        <div className="md:hidden fixed inset-0 z-20 bg-black/40" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
                    )}

                    <Sidebar navigateTo={navigateTo} currentPage={page} isOpen={isSidebarOpen} setOpen={setSidebarOpen} />

                    <main className="flex-1 min-w-0 p-3 sm:p-6 lg:p-10 overflow-y-auto bg-gray-100 font-sans">
                        <ErrorBoundary>
                            <Suspense fallback={<PageLoader />}>
                                {!pageAllowed && settingsReady && (
                                    <div className="max-w-md mx-auto mt-16 text-center bg-white rounded-lg shadow p-8">
                                        <h2 className="text-xl font-semibold text-gray-800 mb-2">This section isn't available</h2>
                                        <p className="text-gray-500 mb-6">Your account doesn't have access to it. Contact your administrator if you need it.</p>
                                        {home && (
                                            <button onClick={() => navigateTo(home)} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                                                Go to {PAGE_TITLES[home]}
                                            </button>
                                        )}
                                    </div>
                                )}
                                {pageAllowed && <>
                                {page === 'dashboard' && <Dashboard navigateTo={navigateTo} />}
                                {page === 'proformas' && <ProformasPage navigateTo={navigateTo} />}
                                {page === 'invoices' && <InvoicesPage navigateTo={navigateTo} />}
                                {page === 'payments' && <PaymentsPage navigateTo={navigateTo} />}
                                {page === 'expenses' && <ExpensesPage />}
                                {page === 'stock' && <StockPage />}
                                {page === 'clients' && <ClientsPage />}
                                {page === 'newDocument' && <NewDocumentPage navigateTo={navigateTo} documentToEdit={editingDocument} />}
                                {page === 'viewDocument' && <ViewDocumentPage documentToView={editingDocument} navigateTo={navigateTo} previousPage={previousPage} />}
                                {page === 'settings' && <SettingsPage navigateTo={navigateTo} />}
                                {page === 'admin' && <AdminPage />}
                                {page === 'accounting' && <AccountingPage />}
                                {page === 'services' && <ServicesPage />}
                                </>}
                            </Suspense>
                        </ErrorBoundary>
                    </main>
                </div>
            </ErrorBoundary>
        );
    };

    return (
        <NavigationGuardContext.Provider value={setNavigationGuard}>
            <div className="antialiased">{renderPage()}</div>
        </NavigationGuardContext.Provider>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <SettingsProvider>
                <AppContent />
            </SettingsProvider>
        </AuthProvider>
    );
}
